import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

import {
	DropdownItem,
	IS_NOT_EMPTY,
	IS_NOT_NULL_OR_UNDEFINED,
	RestService,
} from 'cargo-chief-commons';

import { UrlService } from '@service/url/url.service';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { UtilService } from '@service/util/util.service';
import { SegmentWrapperService } from '@service/segmentWrapper/segment-wrapper.service';
import { LoginSetupService } from '@service/login-setup/login-setup.service';
import { ProductsService } from '@service/products/products.service';
import { UserSettingsService } from '@service/userSettings/user-settings.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { SetPasswordComponent } from '@shared/set-password/set-password.component';
import { CARRIER_LANDING_PAGES, LANDING_PAGES } from '@domain/constants/landing-pages';
import { environment } from '@environment/environment';
import { BaseComponent } from '@shared/base/base.component';
import { takeUntil } from 'rxjs/operators';
import {
	CapacitySearchSettings,
	DefaultCapacitySearchSettings,
} from '@domain/interface/capacity-search-settings';
import {
	CAPACITY_SEARCH_SETTINGS,
	getCapacitySearchTabs,
} from '@domain/constants/capacity-search-settings';
import { CapacitySearchSettingsService } from '@service/capacitySearchSettings/capacity-search-settings.service';
import { MtxPopoverTrigger } from '@ng-matero/extensions/popover';
import { CarrierVettingService } from '@service/carrier-vetting/carrier-vetting.service';
import { IntegrationService } from '@service/integration/integration.service';

@Component({
	selector: 'app-profile-popover',
	templateUrl: './profile-popover.component.html',
	styleUrls: ['./profile-popover.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProfilePopoverComponent extends BaseComponent implements OnInit, OnDestroy {
	@Input() showLogout: boolean = false;
	@Input() highlightLandingPageSection: boolean = false;

	IS_NOT_NULL_OR_UNDEFINED = IS_NOT_NULL_OR_UNDEFINED;

	user;
	profileForm: UntypedFormGroup;
	isProfileRequestInProgress: boolean = false;
	landingPages: DropdownItem[] = [];
	defaultCapacitySearchSettings: DefaultCapacitySearchSettings;
	hideEngageCarrierDialog: boolean;

	@ViewChild(MtxPopoverTrigger) profilePopover: MtxPopoverTrigger;

	capacitySearchSettings: CapacitySearchSettings = {
		pickupDateType: CAPACITY_SEARCH_SETTINGS.pickupDate,
		originDeadHead: 0,
		destinationDeadHead: 0,
		defaultMinimumTruckCount: 0,
		defaultMinimumFrequency: 0,
		defaultTrailerTypes: '',
		defaultCapacitySearchTab: getCapacitySearchTabs(this.productsService.TASKS).Notes.label,
		override: false,
	};

	profileOpenedFromDashboard: boolean = false;
	environment = environment;

	constructor(
		private _formBuilder: UntypedFormBuilder,
		private restService: RestService,
		private urlService: UrlService,
		public cargochief: Cargochief,
		public utilService: UtilService,
		public dialog: MatDialog,
		private loginSetupService: LoginSetupService,
		private segmentWrapper: SegmentWrapperService,
		public productsService: ProductsService,
		public userSettings: UserSettingsService,
		public carrierVettingService: CarrierVettingService,
		private capacitySearchSettingsService: CapacitySearchSettingsService,
		public integrationService: IntegrationService,
	) {
		super();
	}

	ngOnInit() {
		this.defaultCapacitySearchSettings = this.capacitySearchSettingsService.getSettings(
			CAPACITY_SEARCH_SETTINGS.settingType.userSetting
		);
		if (this.userSettings.HIDE_ENGAGE_CARRIER_DIALOG) {
			this.hideEngageCarrierDialog = false;
		}
		this.user = this.cargochief.getLoginSetupUser();
		if (IS_NOT_NULL_OR_UNDEFINED(this.cargochief.getUser())) {
			this.setProfileForm();
		}
		this.populateLandingPages();
	}

	setProfileForm() {
		this.profileForm = this._formBuilder.group({
			firstName: [this.user.firstName, Validators.required],
			lastName: [this.user.lastName, Validators.required],
			phoneNumber: [
				IS_NOT_EMPTY(this.user.phoneNumber) ? this.user.phoneNumber.split(' ext. ')[0] : '',
			],
			phoneNumberExt: [
				IS_NOT_EMPTY(this.user.phoneNumber) ? this.user.phoneNumber.split(' ext. ')[1] : '',
			],
			mobileNumber: [IS_NOT_EMPTY(this.user.mobileNumber) ? this.user.mobileNumber : ''],
			landingPage: [this.user.landingPage],
			showDashboard: [true],
			mcpUsername: [this.user.mcpUsername],
			onboardingLink: [this.user.onboardingLink],
		});
	}

	updateProfile() {
		this.isProfileRequestInProgress = true;
		const url = this.urlService.updateUser();
		let payload = Object.assign({}, this.profileForm.value);
		payload.userId = this.cargochief.USER_ID;
		payload.email = this.cargochief.getUser()['email'];
		payload.userType = this.cargochief.getUser()['userType'];

		if (IS_NOT_EMPTY(payload.phoneNumberExt)) {
			payload.phoneNumber = payload.phoneNumber + ' ext. ' + payload.phoneNumberExt;
			delete payload.phoneNumberExt;
		}
		let request = {
			...payload,
			...this.capacitySearchSettings,
		};
		if (this.hideEngageCarrierDialog) {
			request['hideEngageCarrierDialog'] = false;
		}
		this.restService
			.POST(url, request)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(result) => {
					this.utilService.showSnackbar(
						'Profile updated successfully.',
						SNACKBAR.SUCCESS,
						SNACKBAR.ACTION
					);
					this.loginSetupService.updateLoginSetup();
					this.isProfileRequestInProgress = false;
				},
				(error) => {
					this.utilService.showSnackbar(
						`Unable to update profile now. ${error.error}`,
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
					this.isProfileRequestInProgress = false;
				}
			);
	}

	openSetPasswordDialog() {
		const dialogRef = this.dialog.open(SetPasswordComponent, {
			width: '500px',
			data: {
				showCloseIcon: true,
			},
			disableClose: true,
		});
	}

	closeProfilePopover() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profilePopover)) {
			this.profilePopover.closePopover();
		}
	}

	openProfilePopover() {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profilePopover && this.profilePopover.popoverClosed)) {
			this.profileOpenedFromDashboard = true;
			this.profilePopover.openPopover();
		}
	}

	populateLandingPages() {
		this.landingPages = [];
		let landingPages;
		if (this.utilService.isAccountTypeCarrier()) {
			landingPages = CARRIER_LANDING_PAGES;
			landingPages['PROFILE'] = {
				label: 'Profile',
				value: `carriers/${this.cargochief.CARRIER.id}`,
			};
		} else {
			landingPages = LANDING_PAGES;
		}
		Object.keys(landingPages)?.forEach((key) => {
			this.landingPages.push({
				label: landingPages[key].label,
				value: landingPages[key].value,
			});
		});
	}

	updateShowDashboardValue(value: boolean) {
		if (IS_NOT_NULL_OR_UNDEFINED(this.profileForm)) {
			this.profileForm.controls['showDashboard'].setValue(value);
		}
	}

	onCapacitySearchSettingsChange(payload: CapacitySearchSettings) {
		this.capacitySearchSettings = payload;
	}

	updateHideEngageCarrierDialogSetting() {
		if (this.userSettings?.HIDE_ENGAGE_CARRIER_DIALOG) {
			this.hideEngageCarrierDialog = true;
			this.updateProfile();
		}
	}

	ngOnDestroy(): void {
		super.ngOnDestroy();
	}
}
